import { bffServerAddress } from '../common/MetaSetting';

const CLIENT_ID = 'user-client';
const BFF_API_BASE = `${bffServerAddress}/api/bff`;

interface UserInfo {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  roles?: string[];
  [key: string]: any;
}

class BffAuthService {
  private userInfo: UserInfo | null = null;
  private authenticated: boolean = false;
  private listeners: Array<(authenticated: boolean) => void> = [];
  private initialized: boolean = false;
  private initPromise: Promise<boolean> | null = null;

  constructor() {
    this.loadUserInfoFromStorage();
  }

  subscribe(listener: (authenticated: boolean) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(authenticated: boolean) {
    this.listeners.forEach(listener => listener(authenticated));
  }

  private loadUserInfoFromStorage(): void {
    try {
      const userStr = localStorage.getItem('bff_userinfo');
      if (userStr) {
        this.userInfo = JSON.parse(userStr);
      }
    } catch (e) {
      // ignore
    }
  }

  private saveUserInfoToStorage(): void {
    if (this.userInfo) {
      localStorage.setItem('bff_userinfo', JSON.stringify(this.userInfo));
    } else {
      localStorage.removeItem('bff_userinfo');
    }
  }

  get isAuthenticated(): boolean {
    return this.authenticated;
  }

  getUserInfo(): UserInfo | null {
    return this.userInfo;
  }

  async initSession(): Promise<boolean> {
    if (this.initialized) {
      return this.authenticated;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInitSession();
    return this.initPromise;
  }

  private async doInitSession(): Promise<boolean> {
    try {
      const response = await fetch(`${BFF_API_BASE}/${CLIENT_ID}/session/status`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        this.authenticated = false;
        this.initialized = true;
        this.notifyListeners(false);
        return false;
      }

      const data = await response.json();
      this.authenticated = data.authenticated === true;
      this.initialized = true;

      if (this.authenticated) {
        await this.fetchUserInfo();
      }

      this.notifyListeners(this.authenticated);
      return this.authenticated;
    } catch (error) {
      this.authenticated = false;
      this.initialized = true;
      this.notifyListeners(false);
      return false;
    }
  }

  login(): void {
    window.location.href = `${BFF_API_BASE}/${CLIENT_ID}/authorize`;
  }

  async fetchUserInfo(): Promise<UserInfo | null> {
    try {
      const response = await fetch(`${BFF_API_BASE}/${CLIENT_ID}/userinfo`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.authenticated = false;
          this.notifyListeners(false);
        }
        return null;
      }

      const data = await response.json();
      this.userInfo = data;
      this.saveUserInfoToStorage();
      return this.userInfo;
    } catch (error) {
      return null;
    }
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const response = await fetch(`${BFF_API_BASE}/${CLIENT_ID}/token`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.access_token || null;
    } catch (error) {
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${BFF_API_BASE}/${CLIENT_ID}/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      // ignore
    } finally {
      this.clearAuthState();
      this.notifyListeners(false);
    }
  }

  clearAuthState(): void {
    this.authenticated = false;
    this.userInfo = null;
    this.initialized = false;
    this.initPromise = null;
    localStorage.removeItem('bff_userinfo');
  }

  async checkSessionStatus(): Promise<boolean> {
    try {
      const response = await fetch(`${BFF_API_BASE}/${CLIENT_ID}/session/status`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const wasAuthenticated = this.authenticated;
      this.authenticated = data.authenticated === true;

      if (wasAuthenticated !== this.authenticated) {
        this.notifyListeners(this.authenticated);
      }

      return this.authenticated;
    } catch (error) {
      return false;
    }
  }
}

export const bffAuthService = new BffAuthService();
export type { UserInfo };